import FileUploadButtonInput from "@components/FileUploadButtonInput"
import { HighlightArea } from "@components/HighlightTextArea/HighlightTextArea"
import MarkdownPreview from "@components/MarkdownPreview"
import { FORM_ID } from "@features/builder/DashboardConfiguration/DashboardConfiguration"
import { getHighlightedAreas } from "@features/builder/DashboardConfiguration/getHighlightedAreas"
import {
  DashboardConfigurationFormProps,
  DashboardConfigurationFormValues,
} from "@features/builder/DashboardConfiguration/types"
import { YamlTextArea } from "@features/builder/DashboardConfiguration/YamlEditor/YamlEditor"
import { FileDownload } from "@mui/icons-material"
import DeleteIcon from "@mui/icons-material/Delete"
import { Button, Grid } from "@mui/material"
import { useGetGraphDashboardFileQuery } from "@store/services/dashboardService"
import { downloadFile, readFile } from "@utils/file"
import { ChangeEvent, useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"

export const DashboardConfigurationForm = ({
  graphKey,
  onClear,
  errorMessages,
  onSubmit,
  onSubmitError,
}: DashboardConfigurationFormProps) => {
  const formContext = useFormContext<DashboardConfigurationFormValues>()
  const text = formContext.watch("configuration")

  const { data: fetchedDashboard } = useGetGraphDashboardFileQuery(graphKey)
  const [highlightedAreas, setHighlightedAreas] = useState<HighlightArea[]>([])

  const [markdownContent, setMarkdownContent] = useState("")

  useEffect(() => {
    fetch("src/assets/readme/README-dashboard.md")
      .then((response) => response.text())
      .then((text) => setMarkdownContent(text))
  }, [])

  useEffect(() => {
    const areasToHighlight = getHighlightedAreas(text, errorMessages)

    setHighlightedAreas(areasToHighlight)

    // reset form so it is reset dirty form state to show only highlights
    // only if user didn't modified it after receiving errors
    formContext.reset({ ...formContext.getValues(), configuration: text })

    // eslint-disable-next-line react-hooks/exhaustive-deps -- we do not want to fire it when "text" changes
  }, [errorMessages])

  const handleImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const fileContent = await readFile(file)
    formContext.setValue("configuration", fileContent, { shouldDirty: true })
  }

  const handleDownload = () => {
    const yamlContent = formContext.getValues("configuration")
    const blob = new Blob([yamlContent], { type: "text/plain" })
    const blobUrl = URL.createObjectURL(blob)

    downloadFile(`dashboard-${graphKey}.yaml`, blobUrl)
  }

  return (
    <form
      autoComplete="off"
      style={{ height: "100%" }}
      id={FORM_ID}
      onSubmit={formContext.handleSubmit(onSubmit, onSubmitError)}
    >
      <Grid container direction="column" sx={{ height: "100%" }} gap={1} wrap="nowrap">
        <Grid container item xs="auto" gap={1}>
          <Grid item>
            <FileUploadButtonInput label="Import" accept=".yml, .yaml" onChange={handleImport} />
          </Grid>

          <Grid item>
            <Button
              disabled={!text}
              startIcon={<FileDownload />}
              variant="contained"
              onClick={handleDownload}
            >
              Download
            </Button>
          </Grid>

          <Grid item>
            <Button
              disabled={!fetchedDashboard}
              startIcon={<DeleteIcon />}
              variant="contained"
              onClick={onClear}
            >
              Delete
            </Button>
          </Grid>
        </Grid>
        <Grid container item wrap="nowrap" gap={5} sx={{ overflow: "hidden" }}>
          <Grid item xs>
            <YamlTextArea highlightedAreas={highlightedAreas} />
          </Grid>
          <Grid item xs={6} overflow={"scroll"}>
            <MarkdownPreview source={markdownContent} />
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
}
